<template>
  <div class="flex flex-col gap-12 md:flex-row md:items-center">
    <RevIllustration
      :alt="listing.title"
      :height="48"
      :src="listing.thumbnail.link?.href"
      :width="48"
    />

    <div class="flex flex-col">
      <RevLink
        class="body-1-bold cursor-pointer"
        data-qa="title"
        target="_blank"
        @click="handleOpenListingDetail"
      >
        {{ listing.title }}
      </RevLink>

      <div class="body-2 flex flex-col gap-12 md:flex-row md:items-center">
        <RevTag
          v-if="shouldDisplayNewBatteryLabel"
          :label="i18n(translations.newBattery)"
          variant="info"
        />

        <FormattedMessage
          v-if="listing.dualSim"
          class="flex items-center gap-4"
          :definition="translations.simType"
        >
          <template #simType>
            <span class="body-2-bold">{{ listing.dualSim }}</span>
          </template>
        </FormattedMessage>

        <FormattedMessage
          class="flex items-center gap-4"
          :definition="translations.grade"
        >
          <template #grade>
            <RevTag :label="listing.aestheticGrade.label" variant="secondary" />
          </template>
        </FormattedMessage>

        <span class="max-w-[256px] break-words">
          {{ i18n(translations.listingSKU, { sku: listing.sku }) }}
        </span>

        <span>
          <RevLink
            v-if="publicationState === PUBLICATION_STATE.DISABLED"
            @click="handlePublishListing"
          >
            {{ i18n(translations.publishListing) }}
          </RevLink>

          <RevLink v-else @click="handleArchiveListing">
            {{ i18n(translations.archiveListing) }}
          </RevLink>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'

import type { Listing } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevTag } from '@ds/components/Tag'

import {
  PUBLICATION_STATE,
  SPECIAL_OFFER_TYPES,
} from '~/scopes/listings/Listings.constants'
import {
  QUERY_MODAL_NAME,
  QUERY_MODAL_TAB_NAME,
  TABS_INFO,
} from '~/scopes/listings/pages/index/components/ListingDetailsDrawer/ListingDetailsDrawer.constants'
import { useListings } from '~/scopes/listings/pages/index/composables/useListings'

import translations from '../MinifiedInfo.translations'

const emit = defineEmits(['refresh-listings'])
const props = defineProps<{
  listing: Listing
  publicationState: string
}>()

const i18n = useI18n()
const route = useRoute()
const { publish, archive } = useListings()

const shouldDisplayNewBatteryLabel =
  props.listing.specialOfferType?.value === SPECIAL_OFFER_TYPES.NEW_BATTERY

async function handleOpenListingDetail() {
  await navigateTo({
    ...route,
    query: route.query,
    hash: `#${QUERY_MODAL_NAME}=${props.listing.id}#${QUERY_MODAL_TAB_NAME}=${TABS_INFO[0].tabName}`,
    replace: true,
  })
}

async function handlePublishListing() {
  await publish(props.listing.id)
  emit('refresh-listings')
}

async function handleArchiveListing() {
  await archive(props.listing.id)
  emit('refresh-listings')
}
</script>
